import React, { useState, useEffect } from 'react';
import {
  Form, Input, Button, Upload, Tabs, Card, Space, Row, Col, Divider, Select, InputNumber, message
} from 'antd';
import {
  UploadOutlined, PlusOutlined, MinusCircleOutlined, InboxOutlined, SaveOutlined, CloseOutlined
} from '@ant-design/icons';

const { TextArea } = Input;
const { TabPane } = Tabs;

const AddIndustryForm = ({ editingIndustry, onSave, onCancel }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (editingIndustry) {
      // console.log('Loading industry for edit:', editingIndustry);

      // Transform file paths back to Upload component format. Guards
      // against legacy/malformed data (e.g. a non-string value saved for
      // an older record) so one bad field can't crash the whole form.
      const transformFileData = (filePath) => {
        if (!filePath) return [];
        if (Array.isArray(filePath)) return filePath;
        if (typeof filePath !== 'string') return [];

        // If it's a string path, convert it to Upload format
        // Ensure path doesn't have leading slash when combining with base URL
        const cleanPath = filePath.startsWith('/') ? filePath.substring(1) : filePath;
        return [{
          uid: '-1',
          name: filePath.split('/').pop(),
          status: 'done',
          url: `${process.env.REACT_APP_API_BASE_URL}/${cleanPath}`
        }];
      };

      // Coerce any field that Form.List expects to be an array, so legacy
      // data stored in an unexpected shape can't crash the form.
      const asArray = (value) => (Array.isArray(value) ? value : []);
      const asObject = (value) => (value && typeof value === 'object' && !Array.isArray(value) ? value : {});

      form.setFieldsValue({
        sectorName: editingIndustry.sectorName || '',
        briefDescription: editingIndustry.briefDescription || '',
        sectorIcon: transformFileData(editingIndustry.sectorIcon),
        bannerImage: transformFileData(editingIndustry.bannerImage),
        chairCoChairMembers: asArray(editingIndustry.chairCoChairMembers).map(member => ({
          ...asObject(member),
          photo: transformFileData(member?.photo)
        })),
        regional_overview: editingIndustry.regional_overview || '',
        regional_highlights: asArray(editingIndustry.regional_highlights),
        vidarbhaContribution: asObject(editingIndustry.vidarbhaContribution),
        highlight_docs: asArray(editingIndustry.highlight_docs),
        key_players: asArray(editingIndustry.key_players),
        developments: asArray(editingIndustry.developments),
        policies: asArray(editingIndustry.policies),
        incentives: asArray(editingIndustry.incentives),
        opportunities: asArray(editingIndustry.opportunities),
        media_coverage: asArray(editingIndustry.media_coverage),
        press_releases: asArray(editingIndustry.press_releases),
        blogs: asArray(editingIndustry.blogs),
        events: asArray(editingIndustry.events),
        awards: asArray(editingIndustry.awards),
        resources: asArray(editingIndustry.resources),
        surveys: asArray(editingIndustry.surveys),
        successMetrics: {
          ...asObject(editingIndustry.successMetrics),
          caseStudies: asArray(editingIndustry.successMetrics?.caseStudies),
        },
      });
    } else {
      // Clear form for new industry
      form.resetFields();
    }
  }, [editingIndustry, form]);

  // Helper for file upload fields to normalize event
  const normFile = (e) => {
    //console.log('normFile called with:', e);
    if (Array.isArray(e)) {
      return e;
    }
    return e?.fileList || [];
  };

  // Custom upload handler to prevent auto-upload
  const customRequest = ({ file, onSuccess }) => {
    setTimeout(() => {
      onSuccess("ok");
    }, 0);
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      // Get ALL form values including those not visible
      const allValues = form.getFieldsValue(true);
      // console.log('All form values:', allValues);

      // Check if there are any files to upload
      const hasFiles = (obj) => {
        if (Array.isArray(obj)) {
          return obj.some(item => hasFiles(item));
        } else if (obj !== null && typeof obj === 'object') {
          for (const key in obj) {
            const value = obj[key];
            if (Array.isArray(value) && value.length > 0) {
              const firstItem = value[0];
              if (firstItem && firstItem.originFileObj) {
                return true;
              }
            }
            if (hasFiles(value)) {
              return true;
            }
          }
        }
        return false;
      };

      const containsFiles = hasFiles(allValues);
      // console.log('Contains files to upload:', containsFiles);

      if (containsFiles) {
        // Use FormData for file uploads
        const formData = new FormData();
        
        // First, clean the data to separate files from regular data
        const cleanedData = {};
        const fileMap = new Map();
        
        const extractFiles = (obj, prefix = '') => {
          if (Array.isArray(obj)) {
            const cleanedArray = [];
            obj.forEach((item, index) => {
              const currentPath = prefix ? `${prefix}[${index}]` : `[${index}]`;
              if (item !== null && typeof item === 'object') {
                const cleaned = extractFiles(item, currentPath);
                if (Object.keys(cleaned).length > 0 || typeof cleaned === 'string') {
                  cleanedArray.push(cleaned);
                }
              } else {
                cleanedArray.push(item);
              }
            });
            return cleanedArray;
          } else if (obj !== null && typeof obj === 'object') {
            const cleaned = {};
            for (const key in obj) {
              const value = obj[key];
              const currentPath = prefix ? `${prefix}[${key}]` : key;
              
              // Check if this is a file upload field
              if (Array.isArray(value) && value.length > 0 && value[0]) {
                const firstItem = value[0];
                
                // New file to upload
                if (firstItem.originFileObj) {
                  fileMap.set(currentPath, firstItem.originFileObj);
                  continue; // Don't include in cleaned data
                }
                
                // Existing file with URL
                if (firstItem.url) {
                  let existingPath = firstItem.url;
                  // Remove base URL if present
                  if (existingPath.startsWith('http')) {
                    existingPath = existingPath.replace(`${process.env.REACT_APP_API_BASE_URL}`, '');
                  }
                  // Ensure path starts with single slash
                  existingPath = existingPath.replace(/^\/+/, '/');
                  cleaned[key] = existingPath;
                  continue;
                }
              }
              
              // Skip empty arrays from upload fields
              if (Array.isArray(value) && value.length === 0) {
                continue;
              }
              
              // Process nested objects/arrays
              if (value !== null && typeof value === 'object') {
                const result = extractFiles(value, currentPath);
                if (Array.isArray(result) ? result.length > 0 : Object.keys(result).length > 0) {
                  cleaned[key] = result;
                }
              } else if (value !== undefined && value !== null) {
                cleaned[key] = value;
              }
            }
            return cleaned;
          }
          return obj;
        };
        
        const processedData = extractFiles(allValues);
        
        // Add cleaned data as JSON string
        formData.append('data', JSON.stringify(processedData));
        
        // Add all files
        fileMap.forEach((file, fieldPath) => {
          formData.append(fieldPath, file);
        });
        
        // console.log('Sending FormData with files:', Array.from(fileMap.keys()));
        // console.log('Data JSON:', JSON.stringify(processedData, null, 2));
        
        const url = editingIndustry
          ? `${process.env.REACT_APP_API_BASE_URL}/api/industries/${editingIndustry._id}`
          : `${process.env.REACT_APP_API_BASE_URL}/api/industries`;

        const method = editingIndustry ? 'PUT' : 'POST';

        const response = await fetch(url, {
          method: method,
          body: formData
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Server error response:', errorText);
          throw new Error(`Server returned ${response.status}: ${errorText.substring(0, 200)}`);
        }

        const result = await response.json();

        if (result.success) {
          message.success(result.message || 'Industry saved successfully');
          form.resetFields();
          if (onSave) {
            onSave(result.data);
          }
        } else {
          message.error(result.message || 'Error saving industry');
          console.error('API Error:', result);
        }
      } else {
        // No files - use JSON
        const cleanFormData = (obj) => {
          if (Array.isArray(obj)) {
            return obj.map(item => cleanFormData(item));
          } else if (obj !== null && typeof obj === 'object') {
            const cleaned = {};
            for (const key in obj) {
              const value = obj[key];

              // Handle Upload component - it returns an array
              if (Array.isArray(value)) {
                // Check if this is a file upload array
                if (value.length > 0) {
                  const firstItem = value[0];
                  
                  // Check if it's an existing file with URL
                  if (firstItem && firstItem.url) {
                    cleaned[key] = firstItem.url.replace(`${process.env.REACT_APP_API_BASE_URL}`, '');
                    continue;
                  }
                  
                  // Otherwise, treat as regular array
                  cleaned[key] = cleanFormData(value);
                  continue;
                } else {
                  // Empty file list - skip
                  continue;
                }
              }

              // Skip if the value itself is a fileList object (from Upload component)
              if (value && typeof value === 'object' && value.fileList !== undefined) {
                // This is an Upload component value - skip it
                continue;
              }

              // Keep all other fields
              cleaned[key] = cleanFormData(value);
            }
            return cleaned;
          }
          return obj;
        };

        const cleanedValues = cleanFormData(allValues);

        // Remove MongoDB-specific fields when editing
        const { _id, createdAt, updatedAt, __v, ...dataToSend } = cleanedValues;

        // console.log('Cleaned values (no files):', dataToSend);

        // Send data as raw JSON
        const url = editingIndustry
          ? `${process.env.REACT_APP_API_BASE_URL}/api/industries/${editingIndustry._id}`
          : `${process.env.REACT_APP_API_BASE_URL}/api/industries`;

        const method = editingIndustry ? 'PUT' : 'POST';

        const response = await fetch(url, {
          method: method,
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(dataToSend)
        });

        const result = await response.json();

        if (result.success) {
          message.success(result.message || 'Industry saved successfully');
          form.resetFields();
          if (onSave) {
            onSave(result.data);
          }
        } else {
          message.error(result.message || 'Error saving industry');
          console.error('API Error:', result);
        }
      }
    } catch (error) {
      console.error('Error saving industry:', error);
      message.error('Error saving industry: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Shared style object
  const formStyle = {
    fontFamily: 'Rubik, sans-serif',
    fontSize: '16px',
    fontWeight: 400,
    color: '#000000'
  };

  const headingStyle = {
    fontFamily: 'Rubik, sans-serif',
    fontSize: '19px',
    fontWeight: 400,
    color: '#000000',
    marginBottom: '30px',
    backgroundColor: '#e6d5f0',
  };

  const subHeadingStyle = {
    fontFamily: 'Rubik, sans-serif',
    fontSize: '14px',
    fontWeight: 400,
    color: '#000000',
    marginBottom: '8px'
  };

  return (
    <div style={{
      padding: '0',
      background: '#f0f2f5',
      fontFamily: 'Rubik, sans-serif',
      maxHeight: '90vh',
      overflow: 'auto',
      msOverflowStyle: 'none',  /* IE and Edge */
      scrollbarWidth: 'none',    /* Firefox */
    }}>
      <style>{`
        div::-webkit-scrollbar {
          display: none;  /* Chrome, Safari and Opera */
        }
        .ant-tabs-tab.ant-tabs-tab-active {
          background-color: #e6d5f0 !important;
        }
        .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
          color: #470172 !important;
          font-weight: 500;
        }
        .ant-tabs-tab:hover {
          background-color: #f0e5f5 !important;
        }
        .ant-tabs-tab:hover .ant-tabs-tab-btn {
          color: #470172 !important;
        }
      `}</style>
      <Card variant="borderless" style={{ padding: '16px' }}>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
        >

          {/* --- SECTION 1: GLOBAL SETTINGS --- */}
          <Card
            type="inner"
            title={<span style={{
              fontFamily: 'Rubik, sans-serif',
              fontSize: '19px',
              fontWeight: 400,
              color: '#000000',
              marginBottom: '30px',
            }}>Basic Details</span>}

            styles={{ body: { padding: '16px' } }}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label={<span style={formStyle}>Sector Name</span>}
                  name="sectorName"
                  rules={[{ required: true }]}
                >
                  <Input placeholder="e.g. Agriculture & Food Processing" style={formStyle} size="middle" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  label={<span style={formStyle}>Sector Icon</span>}
                  name="sectorIcon"
                  valuePropName="fileList"
                  getValueFromEvent={normFile}
                >
                  <Upload
                    action={`${process.env.REACT_APP_API_URL}/api/upload`}
                    listType="picture-card"
                    maxCount={1}
                    beforeUpload={(file) => {
                      const isImage = file.type.startsWith('image/');
                      if (!isImage) {
                        message.error('You can only upload image files!');
                      }
                      const isLt5M = file.size / 1024 / 1024 < 5;
                      if (!isLt5M) {
                        message.error('Image must be smaller than 5MB!');
                      }
                      return isImage && isLt5M;
                    }}
                    onChange={(info) => {
                      if (info.file.status === 'done') {
                        message.success(`${info.file.name} uploaded successfully`);
                      } else if (info.file.status === 'error') {
                        message.error(`${info.file.name} upload failed.`);
                      }
                    }}
                  >
                    <div style={formStyle}>
                      <PlusOutlined />
                      <div style={{ marginTop: 8 }}>Upload Icon</div>
                    </div>
                  </Upload>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  label={<span style={formStyle}>Banner Image</span>}
                  name="bannerImage"
                  valuePropName="fileList"
                  getValueFromEvent={normFile}
                >
                  <Upload
                    action={`${process.env.REACT_APP_API_URL}/api/upload`}
                    listType="picture-card"
                    maxCount={1}
                    beforeUpload={(file) => {
                      const isImage = file.type.startsWith('image/');
                      if (!isImage) {
                        message.error('You can only upload image files!');
                      }
                      const isLt5M = file.size / 1024 / 1024 < 5;
                      if (!isLt5M) {
                        message.error('Image must be smaller than 5MB!');
                      }
                      return isImage && isLt5M;
                    }}
                    onChange={(info) => {
                      if (info.file.status === 'done') {
                        message.success(`${info.file.name} uploaded successfully`);
                      } else if (info.file.status === 'error') {
                        message.error(`${info.file.name} upload failed.`);
                      }
                    }}
                  >
                    <div style={formStyle}>
                      <PlusOutlined />
                      <div style={{ marginTop: 8 }}>Upload Banner</div>
                    </div>
                  </Upload>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  label={<span style={formStyle}>Brief Description</span>}
                  name="briefDescription"
                  rules={[{ required: true, message: 'Please enter a brief description' }]}
                >
                  <TextArea
                    rows={2}
                    placeholder="Enter a comprehensive description of the sector..."
                    style={{ ...formStyle, maxHeight: 90, overflowY: 'auto', resize: 'vertical' }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* --- TABS FOR DETAILED SECTIONS --- */}
          <Tabs
            defaultActiveKey="1"
            type="card"
            tabPlacement="left"
            style={{
              ...formStyle,
              minHeight: '400px'
            }}
            tabBarStyle={{
              fontFamily: 'Rubik, sans-serif',
              fontSize: '16px',
              fontWeight: 400,
              color: '#000000',
              width: '200px',
              marginTop: '11px'
            }}
          >

            {/* TAB 1: CONVENERS AND CO-CONVENERS */}
            <TabPane tab={<span style={formStyle}>Conveners and Co-Conveners</span>} key="1">
              <div style={{ minHeight: '350px', padding: '12px' }}>
                <h3 style={headingStyle}>Conveners & Co-Conveners</h3>

                <Form.List name="chairCoChairMembers">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, ...restField }) => (
                        <Card key={key} size="small" style={{ marginBottom: 10 }}>
                          <Row gutter={16}>
                            <Col span={8}>
                              <Form.Item {...restField} name={[name, 'fullName']} label={<span style={formStyle}>Full Name</span>}>
                                <Input placeholder="e.g. Shri. Anuruddha Kshatriya" style={formStyle} />
                              </Form.Item>
                            </Col>
                            <Col span={8}>
                              <Form.Item {...restField} name={[name, 'designation']} label={<span style={formStyle}>Designation</span>}>
                                <Input placeholder="e.g. GM - Investment Promotion" style={formStyle} />
                              </Form.Item>
                            </Col>
                            <Col span={6}>
                              <Form.Item {...restField} name={[name, 'role']} label={<span style={formStyle}>Role</span>}>
                                <Select placeholder="Select Role" style={formStyle}>
                                  <Select.Option value="chair">Convener</Select.Option>
                                  <Select.Option value="cochair">Co-Convener</Select.Option>
                                </Select>
                              </Form.Item>
                            </Col>
                            <Col span={2}>
                              <MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#000000', marginTop: '30px' }} />
                            </Col>
                          </Row>
                          <Row gutter={16}>
                            <Col span={6}>
                              <Form.Item {...restField} name={[name, 'photo']} label={<span style={formStyle}>Profile Photo</span>} valuePropName="fileList" getValueFromEvent={normFile}>
                                <Upload maxCount={1} listType="picture-card">
                                  <div style={formStyle}>
                                    <PlusOutlined />
                                    <div style={{ marginTop: 8 }}>Upload</div>
                                  </div>
                                </Upload>
                              </Form.Item>
                            </Col>
                            <Col span={18}>
                              <Form.Item {...restField} name={[name, 'description']} label={<span style={formStyle}>Description</span>}>
                                <TextArea rows={3} placeholder="Brief description about the member..." style={formStyle} />
                              </Form.Item>
                            </Col>
                          </Row>
                        </Card>
                      ))}
                      <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} style={formStyle}>
                        Add Convener / Co-Convener Member
                      </Button>
                    </>
                  )}
                </Form.List>
              </div>
            </TabPane>

            {/* TAB 2: INDUSTRY OVERVIEW */}
            <TabPane tab={<span style={formStyle}>Industry Overview</span>} key="2">
              <div style={{ minHeight: '350px', padding: '12px' }}>
                <Row gutter={24}>
                  {/* Regional Scenario */}
                  <Col span={24}>
                    <h3 style={headingStyle}>Regional Scenario</h3>
                    <Form.Item label={<span style={formStyle}>Overview Text</span>} name="regional_overview">
                      <TextArea rows={4} style={formStyle} />
                    </Form.Item>

                    <h4 style={subHeadingStyle}>Visual Highlights (Cards)</h4>
                    <Form.List name="regional_highlights">
                      {(fields, { add, remove }) => (
                        <>
                          {fields.map(({ key, name, ...restField }) => (
                            <Card size="small" key={key} style={{ marginBottom: 10 }}>
                              <Space vertical style={{ width: '100%' }}>
                                <Input placeholder="Rank/Title (e.g. Largest)" style={formStyle} />
                                <TextArea placeholder="Description (e.g. Production of Milk...)" rows={2} style={formStyle} />
                                <Upload><Button size="small" style={formStyle}>Upload Icon</Button></Upload>
                                <Button type="text" danger onClick={() => remove(name)} style={formStyle}>Remove Card</Button>
                              </Space>
                            </Card>
                          ))}
                          <Form.Item>
                            <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} style={formStyle}>
                              Add Highlight Card
                            </Button>
                          </Form.Item>
                        </>
                      )}
                    </Form.List>
                  </Col>
                </Row>
              </div>
            </TabPane>

            {/* TAB 3: VIDARBHA CONTRIBUTION */}
            <TabPane tab={<span style={formStyle}>Vidarbha Contribution</span>} key="3">
              <div style={{ minHeight: '350px', padding: '12px' }}>
                <h3 style={headingStyle}>Vidarbha Regional Contribution</h3>
                <Form.Item label={<span style={formStyle}>Overview Text</span>} name={['vidarbhaContribution', 'overview']}>
                  <TextArea rows={4} placeholder="Describe Vidarbha's contribution to this sector..." style={formStyle} />
                </Form.Item>

                <h4 style={subHeadingStyle}>Key Statistics</h4>
                <Form.List name={['vidarbhaContribution', 'statistics']}>
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, ...restField }) => (
                        <Card size="small" key={key} style={{ marginBottom: 10 }}>
                          <Row gutter={16}>
                            <Col span={8}>
                              <Form.Item {...restField} name={[name, 'title']} label={<span style={formStyle}>Statistic Title</span>}>
                                <Input placeholder="e.g. Production Volume" style={formStyle} />
                              </Form.Item>
                            </Col>
                            <Col span={6}>
                              <Form.Item {...restField} name={[name, 'value']} label={<span style={formStyle}>Value</span>}>
                                <Input placeholder="e.g. 25%" style={formStyle} />
                              </Form.Item>
                            </Col>
                            <Col span={8}>
                              <Form.Item {...restField} name={[name, 'description']} label={<span style={formStyle}>Description</span>}>
                                <TextArea rows={2} placeholder="Brief description..." style={formStyle} />
                              </Form.Item>
                            </Col>
                            <Col span={2}>
                              <MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#000000', marginTop: '30px' }} />
                            </Col>
                          </Row>
                        </Card>
                      ))}
                      <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} style={formStyle}>
                        Add Statistic
                      </Button>
                    </>
                  )}
                </Form.List>

                <h4 style={subHeadingStyle}>Major Industries/Companies</h4>
                <Form.List name={['vidarbhaContribution', 'majorIndustries']}>
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, ...restField }) => (
                        <Card size="small" key={key} style={{ marginBottom: 10 }}>
                          <Row gutter={16}>
                            <Col span={10}>
                              <Form.Item {...restField} name={[name, 'name']} label={<span style={formStyle}>Company/Industry Name</span>}>
                                <Input placeholder="Company name" style={formStyle} />
                              </Form.Item>
                            </Col>
                            <Col span={6}>
                              <Form.Item {...restField} name={[name, 'location']} label={<span style={formStyle}>Location</span>}>
                                <Input placeholder="e.g. Nagpur, Amravati" style={formStyle} />
                              </Form.Item>
                            </Col>
                            <Col span={6}>
                              <Form.Item {...restField} name={[name, 'logo']} label={<span style={formStyle}>Logo</span>} valuePropName="fileList" getValueFromEvent={normFile}>
                                <Upload maxCount={1}>
                                  <Button icon={<UploadOutlined />} style={formStyle} size="small">Upload</Button>
                                </Upload>
                              </Form.Item>
                            </Col>
                            <Col span={2}>
                              <MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#000000', marginTop: '30px' }} />
                            </Col>
                          </Row>
                          <Row gutter={16}>
                            <Col span={24}>
                              <Form.Item {...restField} name={[name, 'description']} label={<span style={formStyle}>Description</span>}>
                                <TextArea rows={2} placeholder="Brief description of contribution..." style={formStyle} />
                              </Form.Item>
                            </Col>
                          </Row>
                        </Card>
                      ))}
                      <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} style={formStyle}>
                        Add Industry
                      </Button>
                    </>
                  )}
                </Form.List>
              </div>
            </TabPane>

            {/* TAB 4: SECTOR HIGHLIGHTS (PDF DOWNLOADS) */}
            <TabPane tab={<span style={formStyle}>Sector Highlights</span>} key="4">
              <div style={{ minHeight: '350px', padding: '12px' }}>
                <h3 style={headingStyle}>Sector Highlights Documents</h3>
                <Form.List name="highlight_docs">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, ...restField }) => (
                        <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                          <Form.Item {...restField} name={[name, 'title']} label={<span style={formStyle}>Doc Title</span>}>
                            <Input placeholder="e.g. Agri Crop Protection" style={formStyle} />
                          </Form.Item>
                          <Form.Item {...restField} name={[name, 'file']} valuePropName="fileList" getValueFromEvent={normFile}>
                            <Upload maxCount={1}>
                              <Button icon={<UploadOutlined />} style={formStyle}>Select PDF</Button>
                            </Upload>
                          </Form.Item>
                          <MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#000000' }} />
                        </Space>
                      ))}
                      <Form.Item>
                        <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} style={formStyle}>
                          Add Document
                        </Button>
                      </Form.Item>
                    </>
                  )}
                </Form.List>
              </div>
            </TabPane>

            {/* TAB 5: DOMINANT FORCES (LOGOS) */}
            <TabPane tab={<span style={formStyle}>Dominant Forces</span>} key="5">
              <div style={{ minHeight: '350px', padding: '12px' }}>
                <h3 style={headingStyle}>Key Industry Players</h3>
                <Form.List name="key_players">
                  {(fields, { add, remove }) => (
                    <Row gutter={[16, 16]}>
                      {fields.map(({ key, name, ...restField }) => (
                        <Col span={6} key={key}>
                          <Card
                            size="small"
                            title={<span style={formStyle}>Player {name + 1}</span>}
                            extra={<MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#000000' }} />}
                          >
                            <Form.Item {...restField} name={[name, 'companyName']}>
                              <Input placeholder="Company Name" style={formStyle} />
                            </Form.Item>
                            <Form.Item {...restField} name={[name, 'logo']} valuePropName="fileList" getValueFromEvent={normFile}>
                              <Upload listType="picture-card" maxCount={1}>
                                <div style={formStyle}><PlusOutlined /><div style={{ marginTop: 8 }}>Logo</div></div>
                              </Upload>
                            </Form.Item>
                          </Card>
                        </Col>
                      ))}
                      <Col span={6}>
                        <Button
                          type="dashed"
                          style={{ height: '100%', width: '100%', ...formStyle }}
                          onClick={() => add()}
                          icon={<PlusOutlined />}
                        >
                          Add Player
                        </Button>
                      </Col>
                    </Row>
                  )}
                </Form.List>
              </div>
            </TabPane>

            {/* TAB 6: KEY DEVELOPMENTS */}
            <TabPane tab={<span style={formStyle}>Key Developments</span>} key="6">
              <div style={{ minHeight: '350px', padding: '12px' }}>
                <h3 style={headingStyle}>Recent Key Developments</h3>
                <Form.List name="developments">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, ...restField }) => (
                        <Card key={key} size="small" style={{ marginBottom: 10 }}>
                          <Space align="start">
                            <Form.Item {...restField} name={[name, 'icon']} valuePropName="fileList" getValueFromEvent={normFile}>
                              <Upload listType="picture-card" maxCount={1} customRequest={customRequest}>
                                <div style={formStyle}>Icon</div>
                              </Upload>
                            </Form.Item>
                            <Form.Item {...restField} name={[name, 'description']} style={{ width: 400 }}>
                              <TextArea rows={3} placeholder="Development description..." style={formStyle} />
                            </Form.Item>
                            <MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#000000' }} />
                          </Space>
                        </Card>
                      ))}
                      <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} style={formStyle}>
                        Add Development
                      </Button>
                    </>
                  )}
                </Form.List>
              </div>
            </TabPane>

            {/* TAB 7: POLICIES */}
            <TabPane tab={<span style={formStyle}>Policies</span>} key="7">
              <div style={{ minHeight: '350px', padding: '12px' }}>
                <h3 style={headingStyle}>Government Policies</h3>
                <Form.List name="policies">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, ...restField }) => (
                        <Card key={key} size="small" style={{ marginBottom: 10 }}>
                          <Row gutter={16}>
                            <Col span={10}>
                              <Form.Item {...restField} name={[name, 'policyName']} label={<span style={formStyle}>Policy Name</span>}>
                                <Input placeholder="Policy name" style={formStyle} />
                              </Form.Item>
                            </Col>
                            <Col span={6}>
                              <Form.Item {...restField} name={[name, 'date']} label={<span style={formStyle}>Date</span>}>
                                <Input type="date" style={formStyle} />
                              </Form.Item>
                            </Col>
                            <Col span={6}>
                              <Form.Item {...restField} name={[name, 'file']} label={<span style={formStyle}>Policy File</span>} valuePropName="fileList" getValueFromEvent={normFile}>
                                <Upload maxCount={1}>
                                  <Button icon={<UploadOutlined />} style={formStyle} size="small">Upload PDF</Button>
                                </Upload>
                              </Form.Item>
                            </Col>
                            <Col span={2}>
                              <MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#000000', marginTop: '30px' }} />
                            </Col>
                          </Row>
                          <Row gutter={16}>
                            <Col span={24}>
                              <Form.Item {...restField} name={[name, 'description']} label={<span style={formStyle}>Description</span>}>
                                <TextArea rows={2} placeholder="Policy description..." style={formStyle} />
                              </Form.Item>
                            </Col>
                          </Row>
                        </Card>
                      ))}
                      <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} style={formStyle}>
                        Add Policy
                      </Button>
                    </>
                  )}
                </Form.List>
              </div>
            </TabPane>

            {/* TAB 8: SCHEMES */}
            <TabPane tab={<span style={formStyle}>Schemes</span>} key="8">
              <div style={{ minHeight: '350px', padding: '12px' }}>
                <h3 style={headingStyle}>Incentive Schemes</h3>
                <Form.List name="incentives">
                  {(fields, { add, remove }) => (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {fields.map(({ key, name, ...restField }) => (
                        <Card
                          key={key}
                          size="small"
                          title={<span style={formStyle}>Incentive Category {name + 1}</span>}
                          extra={<Button danger size="small" onClick={() => remove(name)} style={formStyle}>Remove</Button>}
                        >
                          <Form.Item label={<span style={formStyle}>Category Title</span>} name={[name, 'title']}>
                            <Input placeholder="e.g. Capital Subsidy" style={formStyle} />
                          </Form.Item>
                          <Form.Item label={<span style={formStyle}>Details (Bullet points)</span>} name={[name, 'details']}>
                            <TextArea rows={4} style={formStyle} />
                          </Form.Item>
                        </Card>
                      ))}
                      <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} style={formStyle}>
                        Add Incentive Category
                      </Button>
                    </div>
                  )}
                </Form.List>
              </div>
            </TabPane>

            {/* TAB 9: INVESTMENT OPPORTUNITIES */}
            <TabPane tab={<span style={formStyle}> Opportunities </span>} key="9">
              <div style={{ minHeight: '350px', padding: '12px' }}>
                <h3 style={headingStyle}>Investment Opportunities</h3>
                <Form.List name="opportunities">
                  {(fields, { add, remove }) => (
                    <Row gutter={[16, 16]}>
                      {fields.map(({ key, name, ...restField }) => (
                        <Col span={8} key={key}>
                          <Card
                            size="small"
                            title={<span style={formStyle}>Opportunity Card</span>}

                            extra={<MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#000000' }} />}
                          >
                            <Form.Item label={<span style={formStyle}>Type</span>} name={[name, 'type']}>
                              <Input placeholder="e.g. Primary Opportunity" style={formStyle} />
                            </Form.Item>
                            <Form.Item label={<span style={formStyle}>Title</span>} name={[name, 'title']}>
                              <TextArea placeholder="e.g. Processing of Potatoes" rows={2} style={formStyle} />
                            </Form.Item>
                            <Form.Item
                              label={<span style={formStyle}>Icon</span>}
                              name={[name, 'icon']}
                              valuePropName="fileList"
                              getValueFromEvent={normFile}
                            >
                              <Upload maxCount={1}>
                                <Button icon={<UploadOutlined />} style={formStyle}>Icon</Button>
                              </Upload>
                            </Form.Item>
                          </Card>
                        </Col>
                      ))}
                      <Col span={8}>
                        <Button
                          type="dashed"
                          style={{ height: '100%', width: '100%', ...formStyle }}
                          onClick={() => add()}
                          icon={<PlusOutlined />}
                        >
                          Add Opportunity
                        </Button>
                      </Col>
                    </Row>
                  )}
                </Form.List>
              </div>
            </TabPane>

            {/* TAB 10: MEDIA COVERAGE */}
            <TabPane tab={<span style={formStyle}>Media Coverage</span>} key="10">
              <div style={{ minHeight: '350px', padding: '12px' }}>
                <h3 style={headingStyle}>Media Coverage & News</h3>
                <Form.List name="media_coverage">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, ...restField }) => (
                        <Card key={key} size="small" style={{ marginBottom: 10 }}>
                          <Row gutter={16}>
                            <Col span={8}>
                              <Form.Item {...restField} name={[name, 'title']} label={<span style={formStyle}>Title</span>}>
                                <Input placeholder="Media title" style={formStyle} />
                              </Form.Item>
                            </Col>
                            <Col span={6}>
                              <Form.Item {...restField} name={[name, 'sourceName']} label={<span style={formStyle}>Source Name</span>}>
                                <Input placeholder="e.g. Times of India" style={formStyle} />
                              </Form.Item>
                            </Col>
                            <Col span={6}>
                              <Form.Item {...restField} name={[name, 'date']} label={<span style={formStyle}>Date</span>}>
                                <Input type="date" style={formStyle} />
                              </Form.Item>
                            </Col>
                            <Col span={4}>
                              <MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#000000', marginTop: '30px' }} />
                            </Col>
                          </Row>
                          <Row gutter={16}>
                            <Col span={12}>
                              <Form.Item {...restField} name={[name, 'sourceLink']} label={<span style={formStyle}>Source Link</span>}>
                                <Input placeholder="https://..." style={formStyle} />
                              </Form.Item>
                            </Col>
                            <Col span={12}>
                              <Form.Item {...restField} name={[name, 'image']} label={<span style={formStyle}>Image</span>} valuePropName="fileList" getValueFromEvent={normFile}>
                                <Upload maxCount={1}>
                                  <Button icon={<UploadOutlined />} style={formStyle} size="small">Upload</Button>
                                </Upload>
                              </Form.Item>
                            </Col>
                          </Row>
                          <Row gutter={16}>
                            <Col span={24}>
                              <Form.Item {...restField} name={[name, 'description']} label={<span style={formStyle}>Description</span>}>
                                <TextArea rows={2} placeholder="Media coverage description..." style={formStyle} />
                              </Form.Item>
                            </Col>
                          </Row>
                        </Card>
                      ))}
                      <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} style={formStyle}>
                        Add Media Coverage
                      </Button>
                    </>
                  )}
                </Form.List>
              </div>
            </TabPane>

            {/* TAB 11: PRESS RELEASE */}
            <TabPane tab={<span style={formStyle}>Press Release</span>} key="11">
              <div style={{ minHeight: '350px', padding: '12px' }}>
                <h3 style={headingStyle}>Press Releases</h3>
                <Form.List name="press_releases">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, ...restField }) => (
                        <Card key={key} size="small" style={{ marginBottom: 10 }}>
                          <Row gutter={16}>
                            <Col span={8}>
                              <Form.Item {...restField} name={[name, 'title']} label={<span style={formStyle}>Title</span>}>
                                <Input placeholder="Press release title" style={formStyle} />
                              </Form.Item>
                            </Col>
                            <Col span={6}>
                              <Form.Item {...restField} name={[name, 'date']} label={<span style={formStyle}>Date</span>}>
                                <Input type="date" style={formStyle} />
                              </Form.Item>
                            </Col>
                            <Col span={6}>
                              <Form.Item {...restField} name={[name, 'newsArticleLink']} label={<span style={formStyle}>Article Link</span>}>
                                <Input placeholder="https://..." style={formStyle} />
                              </Form.Item>
                            </Col>
                            <Col span={3}>
                              <Form.Item {...restField} name={[name, 'pdf']} label={<span style={formStyle}>PDF</span>} valuePropName="fileList" getValueFromEvent={normFile}>
                                <Upload maxCount={1}>
                                  <Button icon={<UploadOutlined />} style={formStyle} size="small">Upload</Button>
                                </Upload>
                              </Form.Item>
                            </Col>
                            <Col span={1}>
                              <MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#000000', marginTop: '30px' }} />
                            </Col>
                          </Row>
                        </Card>
                      ))}
                      <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} style={formStyle}>
                        Add Press Release
                      </Button>
                    </>
                  )}
                </Form.List>
              </div>
            </TabPane>

            {/* TAB 12: BLOGS */}
            <TabPane tab={<span style={formStyle}>Blogs</span>} key="12">
              <div style={{ minHeight: '350px', padding: '12px' }}>
                <h3 style={headingStyle}>Blog Articles</h3>
                <Form.List name="blogs">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, ...restField }) => (
                        <Card key={key} size="small" style={{ marginBottom: 10 }}>
                          <Row gutter={16}>
                            <Col span={10}>
                              <Form.Item {...restField} name={[name, 'title']} label={<span style={formStyle}>Blog Title</span>}>
                                <Input placeholder="Blog title" style={formStyle} />
                              </Form.Item>
                            </Col>
                            <Col span={6}>
                              <Form.Item {...restField} name={[name, 'author']} label={<span style={formStyle}>Author</span>}>
                                <Input placeholder="Author name" style={formStyle} />
                              </Form.Item>
                            </Col>
                            <Col span={6}>
                              <Form.Item {...restField} name={[name, 'date']} label={<span style={formStyle}>Date</span>}>
                                <Input type="date" style={formStyle} />
                              </Form.Item>
                            </Col>
                            <Col span={2}>
                              <MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#000000', marginTop: '30px' }} />
                            </Col>
                          </Row>
                          <Row gutter={16}>
                            <Col span={24}>
                              <Form.Item {...restField} name={[name, 'content']} label={<span style={formStyle}>Content</span>}>
                                <TextArea rows={3} placeholder="Blog content..." style={formStyle} />
                              </Form.Item>
                            </Col>
                          </Row>
                        </Card>
                      ))}
                      <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} style={formStyle}>
                        Add Blog
                      </Button>
                    </>
                  )}
                </Form.List>
              </div>
            </TabPane>

            {/* TAB 13: EVENTS */}
            <TabPane tab={<span style={formStyle}>Events</span>} key="13">
              <div style={{ minHeight: '350px', padding: '12px' }}>
                <h3 style={headingStyle}>Upcoming & Past Events</h3>
                <Form.List name="events">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, ...restField }) => (
                        <Card key={key} size="small" style={{ marginBottom: 10 }}>
                          <Row gutter={16}>
                            <Col span={8}>
                              <Form.Item {...restField} name={[name, 'title']} label={<span style={formStyle}>Event Title</span>}>
                                <Input placeholder="Event title" style={formStyle} />
                              </Form.Item>
                            </Col>
                            <Col span={6}>
                              <Form.Item {...restField} name={[name, 'date']} label={<span style={formStyle}>Date</span>}>
                                <Input type="date" style={formStyle} />
                              </Form.Item>
                            </Col>
                            <Col span={6}>
                              <Form.Item {...restField} name={[name, 'location']} label={<span style={formStyle}>Location</span>}>
                                <Input placeholder="Event location" style={formStyle} />
                              </Form.Item>
                            </Col>
                            <Col span={4}>
                              <MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#000000', marginTop: '30px' }} />
                            </Col>
                          </Row>
                          <Row gutter={16}>
                            <Col span={24}>
                              <Form.Item {...restField} name={[name, 'description']} label={<span style={formStyle}>Description</span>}>
                                <TextArea rows={2} placeholder="Event description..." style={formStyle} />
                              </Form.Item>
                            </Col>
                          </Row>
                        </Card>
                      ))}
                      <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} style={formStyle}>
                        Add Event
                      </Button>
                    </>
                  )}
                </Form.List>
              </div>
            </TabPane>

            {/* TAB 14: AWARDS & RECOGNITION */}
            <TabPane tab={<span style={formStyle}>Awards & Recognition</span>} key="14">
              <div style={{ minHeight: '350px', padding: '12px' }}>
                <h3 style={headingStyle}>Awards & Recognition</h3>
                <Form.List name="awards">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, ...restField }) => (
                        <Card key={key} size="small" style={{ marginBottom: 10 }}>
                          <Row gutter={16}>
                            <Col span={10}>
                              <Form.Item {...restField} name={[name, 'title']} label={<span style={formStyle}>Award Title</span>}>
                                <Input placeholder="Award title" style={formStyle} />
                              </Form.Item>
                            </Col>
                            <Col span={6}>
                              <Form.Item {...restField} name={[name, 'year']} label={<span style={formStyle}>Year</span>}>
                                <Input placeholder="2024" style={formStyle} />
                              </Form.Item>
                            </Col>
                            <Col span={6}>
                              <Form.Item {...restField} name={[name, 'certificate']} label={<span style={formStyle}>Certificate</span>} valuePropName="fileList" getValueFromEvent={normFile}>
                                <Upload maxCount={1}>
                                  <Button icon={<UploadOutlined />} style={formStyle} size="small">Upload</Button>
                                </Upload>
                              </Form.Item>
                            </Col>
                            <Col span={2}>
                              <MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#000000', marginTop: '30px' }} />
                            </Col>
                          </Row>
                          <Row gutter={16}>
                            <Col span={24}>
                              <Form.Item {...restField} name={[name, 'description']} label={<span style={formStyle}>Description</span>}>
                                <TextArea rows={2} placeholder="Award description..." style={formStyle} />
                              </Form.Item>
                            </Col>
                          </Row>
                        </Card>
                      ))}
                      <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} style={formStyle}>
                        Add Award
                      </Button>
                    </>
                  )}
                </Form.List>
              </div>
            </TabPane>

            {/* TAB 15: RESOURCES & DOWNLOADS */}
            <TabPane tab={<span style={formStyle}>Resources & Downloads</span>} key="15">
              <div style={{ minHeight: '350px', padding: '12px' }}>
                <h3 style={headingStyle}>Resources & Downloads</h3>
                <Form.List name="resources">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, ...restField }) => (
                        <Card key={key} size="small" style={{ marginBottom: 10 }}>
                          <Row gutter={16}>
                            <Col span={10}>
                              <Form.Item {...restField} name={[name, 'title']} label={<span style={formStyle}>Resource Title</span>}>
                                <Input placeholder="Resource title" style={formStyle} />
                              </Form.Item>
                            </Col>
                            <Col span={6}>
                              <Form.Item {...restField} name={[name, 'type']} label={<span style={formStyle}>Type</span>}>
                                <Select placeholder="Select type" style={formStyle}>
                                  <Select.Option value="pdf">PDF</Select.Option>
                                  <Select.Option value="doc">Document</Select.Option>
                                  <Select.Option value="link">External Link</Select.Option>
                                </Select>
                              </Form.Item>
                            </Col>
                            <Col span={6}>
                              <Form.Item {...restField} name={[name, 'file']} label={<span style={formStyle}>File</span>} valuePropName="fileList" getValueFromEvent={normFile}>
                                <Upload maxCount={1}>
                                  <Button icon={<UploadOutlined />} style={formStyle} size="small">Upload</Button>
                                </Upload>
                              </Form.Item>
                            </Col>
                            <Col span={2}>
                              <MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#000000', marginTop: '30px' }} />
                            </Col>
                          </Row>
                          <Row gutter={16}>
                            <Col span={24}>
                              <Form.Item {...restField} name={[name, 'description']} label={<span style={formStyle}>Description</span>}>
                                <TextArea rows={2} placeholder="Resource description..." style={formStyle} />
                              </Form.Item>
                            </Col>
                          </Row>
                        </Card>
                      ))}
                      <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} style={formStyle}>
                        Add Resource
                      </Button>
                    </>
                  )}
                </Form.List>
              </div>
            </TabPane>

            {/* TAB 16: SURVEYS */}
            <TabPane tab={<span style={formStyle}>Surveys</span>} key="16">
              <div style={{ minHeight: '350px', padding: '12px' }}>
                <h3 style={headingStyle}>Industry Surveys</h3>
                <Form.List name="surveys">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, ...restField }) => (
                        <Card key={key} size="small" style={{ marginBottom: 10 }}>
                          <Row gutter={16}>
                            <Col span={10}>
                              <Form.Item {...restField} name={[name, 'title']} label={<span style={formStyle}>Survey Title</span>}>
                                <Input placeholder="Survey title" style={formStyle} />
                              </Form.Item>
                            </Col>
                            <Col span={6}>
                              <Form.Item {...restField} name={[name, 'surveyLink']} label={<span style={formStyle}>Survey Link</span>}>
                                <Input placeholder="https://..." style={formStyle} />
                              </Form.Item>
                            </Col>
                            <Col span={6}>
                              <Form.Item {...restField} name={[name, 'status']} label={<span style={formStyle}>Status</span>}>
                                <Select placeholder="Select status" style={formStyle}>
                                  <Select.Option value="active">Active</Select.Option>
                                  <Select.Option value="closed">Closed</Select.Option>
                                  <Select.Option value="draft">Draft</Select.Option>
                                </Select>
                              </Form.Item>
                            </Col>
                            <Col span={2}>
                              <MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#000000', marginTop: '30px' }} />
                            </Col>
                          </Row>
                          <Row gutter={16}>
                            <Col span={12}>
                              <Form.Item {...restField} name={[name, 'startDate']} label={<span style={formStyle}>Start Date</span>}>
                                <Input type="date" style={formStyle} />
                              </Form.Item>
                            </Col>
                            <Col span={12}>
                              <Form.Item {...restField} name={[name, 'endDate']} label={<span style={formStyle}>End Date</span>}>
                                <Input type="date" style={formStyle} />
                              </Form.Item>
                            </Col>
                          </Row>
                          <Row gutter={16}>
                            <Col span={24}>
                              <Form.Item {...restField} name={[name, 'description']} label={<span style={formStyle}>Description</span>}>
                                <TextArea rows={2} placeholder="Survey description..." style={formStyle} />
                              </Form.Item>
                            </Col>
                          </Row>
                        </Card>
                      ))}
                      <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} style={formStyle}>
                        Add Survey
                      </Button>
                    </>
                  )}
                </Form.List>
              </div>
            </TabPane>

            {/* TAB 17: SUCCESS METRICS */}
            <TabPane tab={<span style={formStyle}>Success Metrics</span>} key="17">
              <div style={{ minHeight: '350px', padding: '12px' }}>
                <h3 style={headingStyle}>Success Stories / Case Studies</h3>

                <Form.List name={['successMetrics', 'caseStudies']}>
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, ...restField }) => (
                        <Card key={key} size="small" style={{ marginBottom: 10 }}>
                          <Row gutter={16}>
                            <Col span={10}>
                              <Form.Item {...restField} name={[name, 'title']} label={<span style={formStyle}>Title</span>}>
                                <Input placeholder="Success story title" style={formStyle} />
                              </Form.Item>
                            </Col>
                            <Col span={6}>
                              <Form.Item {...restField} name={[name, 'companyName']} label={<span style={formStyle}>Company Name</span>}>
                                <Input placeholder="Company name" style={formStyle} />
                              </Form.Item>
                            </Col>
                            <Col span={6}>
                              <Form.Item {...restField} name={[name, 'image']} label={<span style={formStyle}>Image</span>} valuePropName="fileList" getValueFromEvent={normFile}>
                                <Upload maxCount={1}>
                                  <Button icon={<UploadOutlined />} style={formStyle} size="small">Upload</Button>
                                </Upload>
                              </Form.Item>
                            </Col>
                            <Col span={2}>
                              <MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#000000', marginTop: '30px' }} />
                            </Col>
                          </Row>
                          <Row gutter={16}>
                            <Col span={24}>
                              <Form.Item {...restField} name={[name, 'description']} label={<span style={formStyle}>Description</span>}>
                                <TextArea rows={3} placeholder="Detailed success story..." style={formStyle} />
                              </Form.Item>
                            </Col>
                          </Row>
                          <Row gutter={16}>
                            <Col span={8}>
                              <Form.Item {...restField} name={[name, 'investmentAmount']} label={<span style={formStyle}>Investment Amount</span>}>
                                <Input placeholder="e.g. ₹100 Cr" style={formStyle} />
                              </Form.Item>
                            </Col>
                            <Col span={8}>
                              <Form.Item {...restField} name={[name, 'jobsCreated']} label={<span style={formStyle}>Jobs Created</span>}>
                                <InputNumber placeholder="e.g. 500" style={{ ...formStyle, width: '100%' }} />
                              </Form.Item>
                            </Col>
                            <Col span={8}>
                              <Form.Item {...restField} name={[name, 'year']} label={<span style={formStyle}>Year</span>}>
                                <Input placeholder="e.g. 2023" style={formStyle} />
                              </Form.Item>
                            </Col>
                          </Row>
                        </Card>
                      ))}
                      <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} style={formStyle}>
                        Add Case Study
                      </Button>
                    </>
                  )}
                </Form.List>
              </div>
            </TabPane>

          </Tabs>

          {/* Action Buttons - Bottom Right */}
          <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '16px', borderTop: '1px solid #e8e8e8' }}>
            <Button
              icon={<CloseOutlined />}
              onClick={onCancel}
              disabled={loading}
              style={{
                fontFamily: 'Rubik, sans-serif',
                fontSize: '14px',
                fontWeight: 400,
                color: '#470172',
                borderColor: '#470172',
                height: '36px',
                padding: '0 24px'
              }}
            >
              Cancel
            </Button>
            <Button
              icon={<SaveOutlined />}
              type="primary"
              htmlType="submit"
              loading={loading}
              style={{
                fontFamily: 'Rubik, sans-serif',
                fontSize: '14px',
                fontWeight: 400,
                backgroundColor: '#470172',
                borderColor: '#470172',
                height: '36px',
                padding: '0 24px'
              }}
            >
              {loading ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default AddIndustryForm;